import { Button } from '@99designs/common/components/Button';
import { ProgressBar } from '@99designs/common/components/ProgressBar';
import { trackEvent } from '@99designs/common/utils/platform';
import { getConfig, trackCompletedStep } from 'interactive/get-started-steps-configuration';
import useAppState, { clearAppState } from 'interactive/hooks/useAppState';
import { LogoTypeKey } from 'interactive/logo-types';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import Waypoint from 'react-waypoint';

interface InitProps {
    categoryKey: string;
    container: HTMLDivElement;
    variant: string;
}

interface AppProps {
    categoryKey: string;
    variant: string;
}

export function init({ categoryKey, container, variant }: InitProps) {
    ReactDOM.render(<App categoryKey={categoryKey} variant={variant} />, container);
}

const App = ({ categoryKey, variant }: AppProps) => {
    const [isSeenByUser, setIsSeenByUser] = useState(false);
    const stateKey = `get-started-interactive:${categoryKey}`;
    const config = getConfig(variant);
    const [state, setState] = useAppState(stateKey, {
        step: 1,
        logoTypes: [],
    });

    const { step, name, industry, logoTypes } = state;
    const { maxSteps, StepComponent, TitleComponent } = config;
    const comments = getComments(logoTypes);
    const goToNextStep = () => {
        trackCompletedStep(state, categoryKey);
        setState({
            ...state,
            step: step + 1,
        });
    };

    useEffect(() => {
        if (step === 1) {
            window.addEventListener('CtaToInteractive', goToNextStep);

            return () => {
                window.removeEventListener('CtaToInteractive', goToNextStep);
            };
        }
    }, [step]);

    return (
        <Waypoint
            topOffset={'200px'}
            bottomOffset={'200px'}
            fireOnRapidScroll={false}
            onEnter={() => {
                if (!isSeenByUser) {
                    trackEvent('Viewed Prelaunch Interactive', {
                        category: categoryKey,
                    });
                    setIsSeenByUser(true);
                }
            }}
        >
            <div className="get-started-interactive">
                <div className="get-started-interactive__header">
                    <div className="get-started-interactive__header__title">
                        <TitleComponent step={step} />
                    </div>
                    <div className="get-started-interactive__header__progress">
                        <ProgressBar progress={(step / maxSteps) * 100} variations={['small']} />
                    </div>
                </div>
                <div className="get-started-interactive__body">
                    <div className="get-started-interactive__body__inner">
                        <StepComponent
                            step={step}
                            categoryKey={categoryKey}
                            currentState={state}
                            onSave={newState => setState(newState)}
                        />
                    </div>
                </div>
                {step > 1 && (
                    <div className="get-started-interactive__footer">
                        <div className="get-started-interactive__footer__nav">
                            <div className="get-started-interactive__footer__nav__prev">
                                <button
                                    className="icon-button icon-button--lighter icon-button--simple"
                                    onClick={() =>
                                        setState({
                                            ...state,
                                            step: step - 1,
                                        })
                                    }
                                >
                                    <span className="icon icon--left-2" />
                                </button>
                            </div>
                            <div className="get-started-interactive__footer__nav__next">
                                {step === maxSteps ? (
                                    <Button
                                        href={`/launch/${categoryKey}?business_name=${
                                            name ? encodeURIComponent(name) : ''
                                        }&business_industry=${industry || ''}&comments=${comments}`}
                                        variations={['jumbo', 'primary', 'responsive']}
                                        onClick={() => {
                                            trackCompletedStep(state, categoryKey);
                                            clearAppState(stateKey);
                                        }}
                                    >
                                        Next
                                    </Button>
                                ) : (
                                    <Button
                                        variations={['jumbo', 'primary', 'responsive']}
                                        onClick={goToNextStep}
                                    >
                                        Continue
                                    </Button>
                                )}
                            </div>
                        </div>
                        {step === maxSteps ? (
                            <div className="get-started-interactive__footer__bottom-line">
                                <div className="paragraph paragraph--small paragraph--soft paragraph--no-margin">
                                    Discover your brand style…
                                </div>
                            </div>
                        ) : null}
                    </div>
                )}
            </div>
        </Waypoint>
    );
};

const getComments = (logoTypes: LogoTypeKey[]): string => {
    if (logoTypes.length === 0) {
        return '';
    }

    if (logoTypes.indexOf('open-to-ideas') !== -1) {
        if (logoTypes.length === 1) {
            return 'I’m open to ideas on the type of logo that’s best for my brand.';
        } else {
            return `I’m mostly interested in ${specificLogoTypes(
                logoTypes
            )} logos, but I’m open to all ideas.`;
        }
    }

    return `I’m interested in ${specificLogoTypes(logoTypes)} logos.`;
};

const specificLogoTypes = (logoTypes: LogoTypeKey[]) => {
    const types = logoTypes.filter(logoType => logoType !== 'open-to-ideas');
    const length = types.length;

    if (length > 1) {
        return `${types.slice(0, length - 1).join(', ')} and ${types[length - 1]}`;
    } else {
        return types[0];
    }
};
